<template>
  <div>
    <div v-if="asyncDataStatus_ready" class="cms">
      <v-row justify="space-between">
        <v-col cols="12" sm="10" md="6">
          <v-row>
            <v-col cols="12">
              <DateTimeSelector
                labelDate="Od"
                :date="startDate"
                :time="startTime"
                @change="updateStartDateTime"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <DateTimeSelector
                labelDate="Do"
                :date="endDate"
                :time="endTime"
                @change="updateEndDateTime"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                :items="electronicDevicesWithNull"
                item-text="name"
                item-value="val"
                v-model="electronicDevice"
                label="Blagajna"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                :items="usersWithNull"
                item-text="name"
                item-value="val"
                v-model="user"
                label="Uporabnik"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="!searching">
            <v-col cols="12">
              <v-select
                  id="categoriesSelect"
                  class="categoriesSelecta"
                  v-model="selectedCategoryIds"
                  :items="categories"
                  :menu-props="{ maxHeight: '400' }"
                  label="Skupine"
                  multiple
                  hint="Izberite željene skupine ali pustite prazno za vse skupine"
                  persistent-hint
                  item-text="name"
                  item-value="id"
                  clearable
              >
                <template slot="selection" class="categoriesSelecta" slot-scope="{ item, index }">
                  <span>{{ item.name }}</span>
                  <span class="grey--text caption" v-if="index === 1">
                    &nbsp;(+{{ selectedCategoryIds.length - 1 }} others)
                  </span>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row v-if="!searching">
            <v-col cols="12">
              <v-select
                  id="groupsSelect"
                  v-model="selectedGroupsIds"
                  :items="groups"
                  :menu-props="{ maxHeight: '400' }"
                  label="Grupe"
                  multiple
                  height="32"
                  hint="Izberite željene grupe ali pustite prazno za vse grupe"
                  persistent-hint
                  item-text="name"
                  item-value="id"
                  clearable
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-radio-group v-model="subtype">
                <v-radio
                  label="Po skupinah in artiklih"
                  value="BOTH"
                ></v-radio>
                <v-radio
                  label="Po skupinah"
                  value="CATEGORIES"
                ></v-radio>
                <v-radio
                  label="Po artiklih"
                  value="ARTICLES"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="auto">
          <v-row>
            <v-col cols="12">
              <v-btn
                color="#0F998E"
                class="white--text mt-2 float-right d-inlineblock"
                @click="search"
              >
                Išči
                <v-icon right dark>fas fa-search</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-btn
                v-if="!loading && reportData.length"
                color="#0F998E"
                class="white--text mt-2 float-right d-inlineblock"
                @click="print"
              >
                Tiskaj
                <v-icon right dark>fas fa-print</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" v-show="loading">
          <AppSpinner />
        </v-col>
        <v-col cols="12" v-show="!loading">
          <ReportTotalRow :total="total" v-if="!loading && reportData.length" />
          <ReportArticlesItemRow v-for="item in reportData" :key="item.name"
            :item="item"
            :type="subtype"
          />
        </v-col>
      </v-row>
    </div>

    <div class="print">
      <ReportPrintArticles :data="reportData" :params="params" v-if="reportData" />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import asyncDataStatus from '@/mixins/asyncDataStatus'
import DateTimeSelector from '@/components/DateTimeSelector'
import ReportArticlesItemRow from '@/components/ReportArticlesItemRow'
import ReportTotalRow from '@/components/ReportTotalRow'
import ReportPrintArticles from '@/components/ReportPrintArticles'
import { objectToArray } from '../utils/object'

export default {
  components: {
    AppSpinner: () => import('@/components/AppSpinner'),
    ReportPrintArticles,
    DateTimeSelector,
    ReportArticlesItemRow,
    ReportTotalRow
  },
  mixins: [
    asyncDataStatus
  ],
  data () {
    return {
      usersWithNull: [{ name: 'Vsi uporabniki', val: null }],
      user: null,
      electronicDevicesWithNull: [],
      electronicDevice: null,
      selectedCategoryIds: [],
      selectedGroupsIds: [],
      groups: null,
      startDate: '',
      startTime: '00:00',
      endDate: '',
      endTime: '23:59',
      subtype: 'BOTH',
      reportData: [],
      loading: false
    }
  },
  computed: {
    users () {
      return Object.values(this.$store.state.users.items)
    },
    electronicDevices () {
      return Object.values(this.$store.state.devices.items)
    },
    total () {
      return this.reportData.reduce((total, item) => total + item.sum, 0)
    },
    params () {
      return {
        startDate: this.startDate,
        startTime: this.startTime,
        endDate: this.endDate,
        endTime: this.endTime,
        type: 'ARTICLES',
        subtype: this.subtype,
        electronicDevice: this.electronicDevice,
        user: this.user ? this.user : null,
        category: null,
        group: null
      }
    }
  },
  methods: {
    ...mapActions('categories', { loadCategories: 'loadItems' }),
    ...mapActions('groups', { loadGroups: 'loadItems' }),
    ...mapActions('users', { loadUsers: 'loadItems' }),
    ...mapActions('devices', { loadDevices: 'loadItems' }),
    ...mapActions('reports', ['generateReport']),
    updateStartDateTime ({ date, time }) {
      this.startDate = date
      this.startTime = time
    },
    updateEndDateTime ({ date, time }) {
      this.endDate = date
      this.endTime = time
    },
    search () {
      return new Promise((resolve, reject) => {
        this.loading = true
        this.reportData = []
        // console.log(this.params)
        if (this.selectedCategoryIds && this.selectedCategoryIds.length && !this.selectedCategoryIds.includes(null)) {
          this.params.category = this.selectedCategoryIds.join(',')
        } else {
          this.params.category = null
        }
        if (this.selectedGroupsIds && this.selectedGroupsIds.length && !this.selectedGroupsIds.includes(null)) {
          this.params.group = this.selectedGroupsIds.join(',')
        } else {
          this.params.group = null
        }

        this.generateReport(this.params)
          .then(response => {
            this.loading = false
            if (response) {
              this.reportData = response.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
              this.reportData = this.reportData.map(item => {
                if (item.articles) {
                  item.articles = item.articles.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
                }
                return item
              })
              resolve()
            }
          })
          .catch(anErr => {
            this.loading = false
            this.$notify({
              type: 'error',
              text: 'Prišlo je do napake pri generiranju izpisa.'
            })
            resolve()
          })
      })
    },
    print () {
      // let routeData = this.$router.resolve({ name: 'MyCompanyReportsPrint', query: { params: JSON.stringify(this.params) } })
      // window.open(routeData.href, '_blank')
      window.print()
    }
  },
  created () {
    if (!this.$store.state.businessUnits.currentBusinessUnit) {
      this.$notify({ type: 'error', text: 'Potrebna je vsaj ena aktivna poslovna enota' })
    } else {
      const today = moment().format('YYYY-MM-DD')
      this.startDate = today
      this.endDate = today
      Promise.all([
        this.loadDevices(),
        this.loadUsers(),
        this.loadCategories(),
        this.loadGroups()
      ])
        .then(response => {
          this.electronicDevicesWithNull.push({
            name: 'Vse naprave',
            val: null
          })
          this.electronicDevices.forEach(device => {
            this.electronicDevicesWithNull.push({
              name: `${device.name} (${device.electronic_device_id})`,
              val: device.id
            })
          })
          this.users.forEach(user => {
            let name = user.moniker
            if (user.complete_name) {
              name += ` - ${user.complete_name}`
            }
            this.usersWithNull.push({
              name,
              val: user.id
            })
          })
          this.categories = objectToArray(this.$store.state.categories.items)
          this.groups = objectToArray(this.$store.state.groups.items)
          this.search()
            .then(() => {
              this.asyncDataStatus_fetched()
            })
        })
        .catch(anErr => {
          this.$notify({
            type: 'error',
            text: 'Prišlo je do napake pri nalaganju naprav.'
          })
        })
    }
  },
  watch: {
    /* startDate: {
      handler (newVal) {
        if (newVal) this.search()
      },
      immediate: true
    },
    startTime (newVal) {
      if (newVal) this.search()
    },
    endDate (newVal) {
      if (newVal) this.search()
    },
    endTime (newVal) {
      if (newVal) this.search()
    },
    electronicDevice () {
      if (this.asyncDataStatus_ready) this.search()
    },
    subtype () {
      if (this.asyncDataStatus_ready) this.search()
    } */
  }
}
</script>

<style lang="scss" scoped>
.print {
  display: none;
}
@media print {
  .print {
    display: block;
  }
}
</style>

<style lang="scss">
@media print {
  .cms,
  .header,
  .v-navigation-drawer,
  .footer {
    display: none;
  }
  .v-content {
    padding: 0px !important;
  }
}
</style>
